import type {
  Box,
  FigureSpec,
  HitRegion,
  Point,
  ResolvedFigure,
  SceneAnchor,
  SceneShape,
  SemanticRelation,
  ShapeRole,
  TextShape,
} from './model.js';

export const roundGeometry = (value: number): number => Math.round(value * 64) / 64;
export const point = (x: number, y: number): Point => ({ x: roundGeometry(x), y: roundGeometry(y) });
export const box = (x: number, y: number, width: number, height: number): Box => ({
  x: roundGeometry(x),
  y: roundGeometry(y),
  width: roundGeometry(width),
  height: roundGeometry(height),
});

export class SceneBuilder {
  readonly shapes: SceneShape[] = [];
  readonly anchors: SceneAnchor[] = [];
  readonly hitRegions: HitRegion[] = [];
  readonly relations: SemanticRelation[] = [];

  rect(id: string, semanticId: string, role: ShapeRole, bounds: Box, options: { radius?: number; label?: string; interactive?: boolean } = {}): void {
    this.shapes.push({ kind: 'rect', id, semanticId, role, box: bounds, ...options });
  }

  polygon(id: string, semanticId: string, role: ShapeRole, points: Point[], label?: string): void {
    this.shapes.push({ kind: 'polygon', id, semanticId, role, points, label });
  }

  path(id: string, semanticId: string, role: ShapeRole, d: string, options: { markerEnd?: boolean; dash?: string; label?: string } = {}): void {
    this.shapes.push({ kind: 'path', id, semanticId, role, d, ...options });
  }

  line(id: string, semanticId: string, role: ShapeRole, from: Point, to: Point, options: { markerStart?: boolean; markerEnd?: boolean; dash?: string } = {}): void {
    this.shapes.push({ kind: 'line', id, semanticId, role, from, to, ...options });
  }

  text(id: string, semanticId: string, role: ShapeRole, at: Point, text: string, options: Partial<Omit<TextShape, 'kind' | 'id' | 'semanticId' | 'role' | 'at' | 'text'>> = {}): void {
    this.shapes.push({
      kind: 'text',
      id,
      semanticId,
      role,
      at,
      text,
      size: options.size ?? 12,
      align: options.align ?? 'start',
      weight: options.weight,
      mono: options.mono,
      uppercase: options.uppercase,
      rotate: options.rotate,
    });
  }

  anchor(id: string, semanticId: string, at: Point, direction?: SceneAnchor['direction']): void {
    this.anchors.push({ id, semanticId, point: at, direction });
  }

  hit(semanticId: string, bounds: Box): void {
    this.hitRegions.push({ semanticId, box: bounds });
  }

  relation(id: string, type: string, from: string, to: string, label: string): void {
    this.relations.push({ id, type, from, to, label });
  }
}

export function finishFigure(
  spec: FigureSpec,
  builder: SceneBuilder,
  data: { title: string; subtitle: string; state: string; active: string[]; warnings?: string[] },
): ResolvedFigure {
  const geometry = {
    viewport: spec.viewport,
    shapes: builder.shapes.map((shape) => {
      if (shape.kind === 'rect') return { kind: shape.kind, id: shape.id, box: shape.box };
      if (shape.kind === 'polygon') return { kind: shape.kind, id: shape.id, points: shape.points };
      if (shape.kind === 'path') return { kind: shape.kind, id: shape.id, d: shape.d };
      if (shape.kind === 'line') return { kind: shape.kind, id: shape.id, from: shape.from, to: shape.to };
      return { kind: shape.kind, id: shape.id, at: shape.at, text: shape.text, size: shape.size, align: shape.align, rotate: shape.rotate };
    }),
    anchors: builder.anchors,
    hits: builder.hitRegions,
  };
  return {
    id: spec.id,
    family: spec.family,
    view: spec.view,
    viewport: spec.viewport,
    title: data.title,
    subtitle: data.subtitle,
    state: data.state,
    provenance: spec.provenance,
    shapes: builder.shapes,
    anchors: builder.anchors,
    hitRegions: builder.hitRegions,
    relations: builder.relations,
    activeSemanticIds: data.active,
    warnings: data.warnings ?? [],
    geometryFingerprint: fnv1a(JSON.stringify(geometry)),
  };
}

export function validateViewport(width: number, height: number): void {
  if (width < 320 || height < 380) throw new Error('view compiler: viewport below supported minimum');
}

function fnv1a(value: string): string {
  let hash = 0x811c9dc5;
  for (let index = 0; index < value.length; index += 1) {
    hash ^= value.charCodeAt(index);
    hash = Math.imul(hash, 0x01000193);
  }
  return (hash >>> 0).toString(16).padStart(8, '0');
}
